import {
  JOB_ATTACHMENT_KINDS,
  MAX_JOB_ATTACHMENTS,
  UPLOAD_RULES,
  type CreatedUpload,
  type UploadInfo,
} from '@hemmafixa/shared';
import { FileGallery, FileUpload, toast, type FileGalleryItem } from '@hemmafixa/ui';
import { useCallback } from 'react';
import { fill, useI18n } from '@/i18n/context';
import { galleryItem, isImage, uploadErrorMessage, useFileTexts, useFileUploads } from './file-uploads';

/** Everything a job accepts: photos and documents (PDF) */
const ACCEPT = [...new Set(JOB_ATTACHMENT_KINDS.flatMap((kind) => UPLOAD_RULES[kind].types))];
const MAX_BYTES = Math.max(...JOB_ATTACHMENT_KINDS.map((kind) => UPLOAD_RULES[kind].maxBytes));

/** A job's photos and documents, read only (admin review, providers). */
export function JobFiles({ files }: { files: UploadInfo[] }) {
  const { t } = useI18n();
  const texts = useFileTexts();
  if (files.length === 0) return <p className="text-sm text-muted-foreground">{t.files.none}</p>;
  return <FileGallery items={files.map(galleryItem)} labels={texts.gallery} />;
}

interface JobFilesEditorProps {
  files: UploadInfo[];
  /** While the job can still change; otherwise the files are only shown */
  editable: boolean;
  create: (input: {
    kind: 'job_photo' | 'job_document';
    fileName: string;
    contentType: string;
    sizeBytes: number;
  }) => Promise<CreatedUpload>;
  complete: (uploadId: string) => Promise<UploadInfo>;
  remove: (uploadId: string) => Promise<void>;
  /** Reloads the job so the list shows the saved files */
  refresh: () => void;
}

/** The client's photos and documents on their job: add (straight to storage), see and remove while in review. */
export function JobFilesEditor({ files, editable, create, complete, remove, refresh }: JobFilesEditorProps) {
  const { t } = useI18n();
  const f = t.files;
  const texts = useFileTexts();
  const { pending, upload, dismiss } = useFileUploads({
    create: useCallback(
      (file: File) =>
        create({
          kind: isImage(file.type) ? 'job_photo' : 'job_document',
          fileName: file.name,
          contentType: file.type,
          sizeBytes: file.size,
        }),
      [create],
    ),
    complete,
    onSettled: refresh,
    errorMessage: useCallback((error: unknown) => uploadErrorMessage(error, f.failed, f.wrongFile), [f]),
  });

  const items = [...files.map(galleryItem), ...pending];
  const room =
    MAX_JOB_ATTACHMENTS - files.length - pending.filter((item) => item.status === 'uploading').length;

  const onRemove = (item: FileGalleryItem) => {
    if (item.id.startsWith('local-')) return dismiss(item.id);
    remove(item.id)
      .then(() => {
        toast.success(f.removed);
        refresh();
      })
      .catch(() => toast.error(f.removeFailed));
  };

  return (
    <div className="grid gap-4">
      {items.length > 0 ? (
        <FileGallery items={items} labels={texts.gallery} onRemove={editable ? onRemove : undefined} />
      ) : (
        !editable && <p className="text-sm text-muted-foreground">{f.none}</p>
      )}
      {editable && room > 0 && (
        <FileUpload
          accept={ACCEPT}
          maxSizeBytes={MAX_BYTES}
          maxFiles={room}
          files={[]}
          onFilesChange={(chosen) => void upload(chosen)}
          onReject={(message) => toast.error(message)}
          label={f.drop}
          hint={fill(f.hint, { max: MAX_JOB_ATTACHMENTS })}
          labels={texts.upload}
        />
      )}
    </div>
  );
}
